/**
 * One entry per service = one route, one sitemap URL, one set of meta tags,
 * one Service + FAQPage schema block. Add an object here and the page, the
 * nav, the sitemap and the structured data all follow automatically.
 *
 * CommonJS: consumed by webpack and by scripts/generate-seo.js.
 */
module.exports = [
  {
    slug: 'web-development',
    name: 'Web Development',
    icon: 'web_development',
    tagline: 'Fast, accessible websites that turn visitors into customers',
    metaTitle: 'Web Development Services | Codelithic Labs',
    metaDescription:
      'Custom web development from a Calgary-based team working worldwide. Fast, accessible, search-friendly websites and web apps in React and Next.js.',
    intro:
      'A website is usually the first thing a customer meets and the last thing that gets attention. We build sites that load quickly, rank well and hold up as your business grows.',
    body: [
      'We build with React, Next.js and TypeScript on infrastructure that scales down to nothing when idle and up when a campaign lands. Every build ships with server-rendered HTML, real structured data and Core Web Vitals budgets enforced in CI, so performance doesn’t quietly erode three months after launch.',
      'If you already have a site, we usually start with an audit: what’s slow, what’s unindexed, what’s costing you conversions. Often the fastest win isn’t a rebuild.',
    ],
    features: [
      { title: 'Performance budgets', desc: 'Core Web Vitals targets set up front and enforced on every deploy.' },
      { title: 'SEO foundations', desc: 'Server-rendered markup, structured data and clean information architecture.' },
      { title: 'Accessibility', desc: 'WCAG 2.2 AA as a baseline, tested with real assistive technology.' },
      { title: 'CMS you can use', desc: 'Content editing your team can handle without filing a ticket.' },
      { title: 'Analytics that answer questions', desc: 'Measurement wired to business outcomes, not vanity pageviews.' },
      { title: 'Maintenance', desc: 'Dependency updates, uptime monitoring and a person who answers.' },
    ],
    tech: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Node.js', 'Vercel', 'AWS'],
    faqs: [
      {
        q: 'How long does a website project take?',
        a: 'A focused marketing site is typically 4–8 weeks from kickoff to launch. Larger builds with custom integrations or a content migration usually run 10–16 weeks. We give you a firm range after discovery, not before.',
      },
      {
        q: 'Do you work with our existing site?',
        a: 'Often, yes. If the foundation is sound we would rather improve what you have than bill you for a rebuild. We start with an audit and tell you honestly which path costs less over two years.',
      },
      {
        q: 'Will we be able to edit content ourselves?',
        a: 'Yes. We set up a CMS matched to how your team works and train them on it. You should never need to pay us to change a headline.',
      },
      {
        q: 'Do you handle hosting?',
        a: 'We can. Most clients run on AWS or Vercel under their own account, so you own the infrastructure and we manage it. You’re never locked in to us.',
      },
    ],
  },
  {
    slug: 'mobile-app-development',
    name: 'Mobile App Development',
    icon: 'mobile_app',
    tagline: 'iOS and Android apps people keep on their home screen',
    metaTitle: 'Mobile App Development Services | Codelithic Labs',
    metaDescription:
      'iOS and Android app development, remote worldwide from our Calgary base. React Native and native builds, from prototype through store launch and support.',
    intro:
      'Most apps get deleted within a week. The ones that survive solve a real problem, start fast, and keep working when the signal drops.',
    body: [
      'We build cross-platform with React Native when it fits and go native with Swift or Kotlin when the product needs it. That decision gets made on your requirements, not on our preference, and we’ll show you the trade-off in writing.',
      'Store submission is part of the work, not an afterthought. We handle review guidelines, privacy declarations, staged rollout and crash monitoring from the first release onward.',
    ],
    features: [
      { title: 'Offline-first', desc: 'Local state and sync so the app keeps working in an elevator or a basement.' },
      { title: 'One codebase where sensible', desc: 'React Native for shared logic, native modules where performance demands it.' },
      { title: 'Store submission handled', desc: 'App Store and Google Play review, privacy labels and phased releases.' },
      { title: 'Push that people tolerate', desc: 'Notifications tied to user intent rather than engagement metrics.' },
      { title: 'Crash and performance monitoring', desc: 'You hear about problems from us, not from a one-star review.' },
      { title: 'Analytics and experimentation', desc: 'Funnel instrumentation and A/B testing wired in from launch.' },
    ],
    tech: ['React Native', 'Swift', 'Kotlin', 'Expo', 'Firebase', 'GraphQL'],
    faqs: [
      {
        q: 'Native or cross-platform: which do we need?',
        a: 'Cross-platform covers most business apps at lower cost. You need native when you’re pushing hardware: heavy graphics, continuous background location, advanced camera work. We make that call with you during discovery.',
      },
      {
        q: 'What does it cost to maintain an app?',
        a: 'Budget for it. iOS and Android each ship a major release every year, and an unmaintained app breaks within about eighteen months. Ongoing support is typically 15–20% of the original build cost per year.',
      },
      {
        q: 'Can you take over an existing app?',
        a: 'Yes. We start with a code and architecture review so you get an honest picture of what you own before committing to a roadmap.',
      },
    ],
  },
  {
    slug: 'ui-ux-design',
    name: 'UI/UX Design',
    icon: 'ux_design',
    tagline: 'Interfaces that make the right action the obvious one',
    metaTitle: 'UI/UX Design Services | Codelithic Labs',
    metaDescription:
      'User experience and interface design for teams anywhere. Research, prototyping, design systems and usability testing that improve conversion and retention.',
    intro:
      'Design is what decides whether someone finishes the task or quietly gives up. That makes it a business problem, not a decorative one.',
    body: [
      'We start with research, watching real people attempt real tasks in your product, because most usability problems are invisible from the inside. From there we move through information architecture, wireframes and interactive prototypes you can put in front of users before anything is built.',
      'What we hand over is a design system, not a pile of screens: tokens, components and states your developers can implement without guessing what happens on error.',
    ],
    features: [
      { title: 'User research', desc: 'Interviews and task-based testing with people who resemble your customers.' },
      { title: 'Information architecture', desc: 'Structure and navigation that match how users think.' },
      { title: 'Interactive prototypes', desc: 'Clickable flows tested with users before a line of code is written.' },
      { title: 'Design systems', desc: 'Reusable tokens and components that keep the product coherent as it grows.' },
      { title: 'Accessibility built in', desc: 'Contrast, focus order and screen-reader behaviour designed, not retrofitted.' },
      { title: 'Developer handoff', desc: 'Specs, states and edge cases documented so nothing gets invented in code.' },
    ],
    tech: ['Figma', 'Framer', 'Storybook', 'Maze', 'Design tokens'],
    faqs: [
      {
        q: 'Do we need research if we already know our users?',
        a: 'Usually yes. Teams know their users well in aggregate and are often surprised by where individuals get stuck. Even five moderated sessions tend to surface issues nobody had predicted.',
      },
      {
        q: 'Can you work with our existing brand?',
        a: 'Yes. We can work entirely inside your brand guidelines, or extend them into a proper digital design system if they only cover print and logo usage.',
      },
      {
        q: 'Do you do design without development?',
        a: 'Yes. Plenty of clients bring us in for design and hand off to their own engineering team. The handoff package is built for exactly that.',
      },
    ],
  },
  {
    slug: 'digital-marketing',
    name: 'Digital Marketing',
    icon: 'digital_marketing',
    tagline: 'Growth you can trace back to revenue',
    metaTitle: 'Digital Marketing & SEO Services | Codelithic Labs',
    metaDescription:
      'SEO, content and paid acquisition measured against pipeline, not impressions. Technical SEO, local search and analytics, delivered remotely worldwide.',
    intro:
      'We care about the number of qualified leads in your pipeline. Impressions, reach and engagement are diagnostics, not goals.',
    body: [
      'Our marketing work starts technical, because it’s hard to rank a site that crawlers can’t read. Once the foundation is sound we move to content built around real search demand and the questions your sales team answers repeatedly.',
      'For local businesses, Google Business Profile and local search often outperform everything else combined. We treat that as a first-class channel rather than an afterthought.',
    ],
    features: [
      { title: 'Technical SEO', desc: 'Crawlability, structured data, Core Web Vitals and index coverage.' },
      { title: 'Local search', desc: 'Google Business Profile, citations and review strategy for Calgary and Alberta.' },
      { title: 'Content strategy', desc: 'Topics chosen from real search demand and real sales objections.' },
      { title: 'Paid acquisition', desc: 'Search and social campaigns with conversion tracking that survives audit.' },
      { title: 'Analytics and attribution', desc: 'GA4 and server-side tracking wired to pipeline, not pageviews.' },
      { title: 'Reporting you can read', desc: 'Monthly reporting in plain language, with what we plan to change next.' },
    ],
    tech: ['Google Analytics 4', 'Search Console', 'Google Ads', 'Meta Ads', 'Ahrefs', 'Looker Studio'],
    faqs: [
      {
        q: 'How long until SEO shows results?',
        a: 'Technical fixes can move things within weeks. Content and authority realistically take three to six months to compound. Anyone promising first-page rankings in thirty days is either lucky or lying.',
      },
      {
        q: 'Do you require a long contract?',
        a: 'No. We work month to month after an initial three-month period, which is the minimum honest window to judge whether the work is producing results.',
      },
      {
        q: 'Who owns the accounts and data?',
        a: 'You do, always. Ad accounts, analytics properties and content live under your ownership. If we part ways you keep everything.',
      },
    ],
  },
  {
    slug: 'cloud-solutions',
    name: 'Cloud Solutions',
    icon: 'cloud_solutions',
    tagline: 'Infrastructure that scales without a surprise invoice',
    metaTitle: 'Cloud Solutions & AWS Consulting | Codelithic Labs',
    metaDescription:
      'Cloud architecture, migration and cost optimisation on AWS and Azure. Infrastructure as code, CI/CD and observability for predictable spend, worldwide.',
    intro:
      'Done well, cloud is invisible and cheap. Done badly, you get a monthly bill nobody can explain and an outage nobody can diagnose.',
    body: [
      'We design infrastructure as code from the start. Terraform, versioned, reviewable, reproducible. No console clicking, no undocumented state, no single person who knows how production was assembled.',
      'Migrations get planned around your risk tolerance. Most clients move in stages with a rollback path at every step, because a big-bang cutover on a Friday is how weekends get ruined.',
    ],
    features: [
      { title: 'Infrastructure as code', desc: 'Terraform modules under version control and code review.' },
      { title: 'Cost optimisation', desc: 'Right-sizing, lifecycle policies and spend alerting before the invoice lands.' },
      { title: 'CI/CD pipelines', desc: 'Automated build, test and deploy with a rollback that works.' },
      { title: 'Observability', desc: 'Logs, metrics and traces so you diagnose incidents instead of guessing.' },
      { title: 'Staged migration', desc: 'Move in increments with a tested rollback at every checkpoint.' },
      { title: 'Disaster recovery', desc: 'Backup and recovery objectives agreed with you, then rehearsed.' },
    ],
    tech: ['AWS', 'Azure', 'Terraform', 'Docker', 'Kubernetes', 'GitHub Actions', 'CloudWatch'],
    faqs: [
      {
        q: 'Can you reduce our current cloud bill?',
        a: 'Usually. Idle resources, oversized instances and forgotten storage are near-universal. We start with a cost review and tell you the realistic savings before you commit to any work.',
      },
      {
        q: 'AWS or Azure?',
        a: 'Both are capable. The right answer usually follows your existing stack and your team’s experience. If you’re heavily invested in Microsoft 365 and Entra ID, Azure tends to win on integration.',
      },
      {
        q: 'Will migration mean downtime?',
        a: 'Most migrations we run have no customer-visible downtime, using staged cutover and traffic shifting. Where a brief window is unavoidable we schedule and rehearse it.',
      },
    ],
  },
  {
    slug: 'software-development',
    name: 'Software Development',
    icon: 'software_development',
    tagline: 'Custom systems built around how your business runs',
    metaTitle: 'Custom Software Development | Codelithic Labs',
    metaDescription:
      'Custom software development for clients worldwide. Internal tools, integrations, APIs and platform modernisation built around your workflow, not a template.',
    intro:
      'If a product is forcing you to change how you work, something has gone backwards. Custom development earns its cost when your process is the thing customers are paying for.',
    body: [
      'We build internal platforms, integrations and the systems that sit between tools that were never designed to talk to each other. Typical projects replace a fragile spreadsheet, a manual re-keying step or a vendor product that fits about 60% of the job.',
      'Everything ships with tests, documentation and a deployment pipeline. You get a system another team could pick up and maintain, which is the point, because you shouldn’t be locked in to whoever wrote it.',
    ],
    features: [
      { title: 'Systems integration', desc: 'Making your existing tools exchange data reliably and observably.' },
      { title: 'Internal tooling', desc: 'Replacing brittle spreadsheets and manual steps with software that holds.' },
      { title: 'API design', desc: 'Versioned, documented interfaces your partners can build against.' },
      { title: 'Legacy modernisation', desc: 'Incremental replacement of aging systems without a risky rewrite.' },
      { title: 'Automated testing', desc: 'Test suites that let you change the system confidently a year from now.' },
      { title: 'Documentation and handover', desc: 'Written so a new developer can be productive without calling us.' },
    ],
    tech: ['TypeScript', 'Node.js', 'Python', 'PostgreSQL', 'Redis', 'REST', 'GraphQL'],
    faqs: [
      {
        q: 'Should we build or buy?',
        a: 'Buy whenever a product fits. It’s almost always cheaper. Build when the process is your differentiator, or when the gap between the product and your workflow is costing real money every month. We’ll tell you when buying is the better answer.',
      },
      {
        q: 'What happens if we stop working with you?',
        a: 'You keep everything: source code, infrastructure, documentation and credentials, all under your ownership throughout. Clean handover is part of the engagement, not a negotiation at the end.',
      },
      {
        q: 'How do you handle changing requirements?',
        a: 'We work in short iterations with working software at the end of each, so changing direction costs weeks rather than quarters. Requirements always change; the process should expect it.',
      },
    ],
  },
  {
    slug: 'consulting',
    name: 'Technology Consulting',
    icon: 'consulting',
    tagline: 'A straight answer on what to build, buy or stop doing',
    metaTitle: 'Technology Consulting & Fractional CTO | Codelithic Labs',
    metaDescription:
      'Architecture review, technical due diligence, roadmap planning and fractional CTO support for teams anywhere, from our Calgary base.',
    intro:
      'Sometimes the most valuable thing we deliver is a recommendation not to build something.',
    body: [
      'We’re brought in when a decision is expensive and the internal answer is contested: whether an architecture will survive the next order of magnitude, whether a vendor is worth the lock-in, whether a team needs more people or different structure.',
      'Engagements are on purpose short and specific. You get a written assessment with a recommendation and the reasoning behind it, not an open-ended retainer.',
    ],
    features: [
      { title: 'Architecture review', desc: 'An honest assessment of whether your system survives the next stage of growth.' },
      { title: 'Technical due diligence', desc: 'Code, infrastructure and team assessment ahead of an acquisition or investment.' },
      { title: 'Roadmap planning', desc: 'Sequencing work so the highest-risk unknowns get resolved earliest.' },
      { title: 'Vendor selection', desc: 'Structured evaluation without a referral fee shaping the recommendation.' },
      { title: 'Fractional CTO', desc: 'Senior technical leadership part-time, for companies not ready to hire one.' },
      { title: 'Team and process', desc: 'Practical changes to how your engineers plan, review and ship.' },
    ],
    tech: ['Architecture review', 'Due diligence', 'Roadmapping', 'Vendor evaluation'],
    faqs: [
      {
        q: 'How long is a typical consulting engagement?',
        a: 'Most are two to six weeks and end with a written assessment and recommendation. Fractional CTO arrangements are ongoing, usually one to two days a week.',
      },
      {
        q: 'Will you recommend your own services?',
        a: 'Only where we’re the right fit, and we’ll say so explicitly when we have an interest. A recommendation you can’t trust is worth nothing, which is why we regularly advise clients to buy a product or hire internally instead.',
      },
      {
        q: 'Do you sign NDAs?',
        a: 'Routinely, and we’re comfortable with due diligence work under strict confidentiality.',
      },
    ],
  },
  {
    slug: 'cybersecurity',
    name: 'Cybersecurity',
    icon: 'cybersecurity',
    tagline: 'Find the gaps before somebody else does',
    metaTitle: 'Cybersecurity Services & Audits | Codelithic Labs',
    metaDescription:
      'Security audits, vulnerability assessments, penetration testing, compliance readiness and incident response planning, delivered remotely worldwide.',
    intro:
      'Most breaches aren’t sophisticated. They’re an unpatched dependency, an over-permissioned account or a password that was reused.',
    body: [
      'We assess what you run: application, infrastructure and the human process around both. Then we hand back a prioritised list of what to fix, ordered by real risk rather than scanner severity. A critical finding on a system with no sensitive data matters less than a medium one on your payment path.',
      'For organisations working toward SOC 2, PIPEDA or sector-specific requirements, we map findings to the relevant controls so remediation and compliance are one piece of work rather than two.',
    ],
    features: [
      { title: 'Security audits', desc: 'Application, infrastructure and access review against real attack paths.' },
      { title: 'Vulnerability assessment', desc: 'Automated and manual testing, triaged by actual business risk.' },
      { title: 'Penetration testing', desc: 'Authorised, scoped testing with a report your board can read.' },
      { title: 'Compliance readiness', desc: 'SOC 2, PIPEDA and sector requirements mapped to concrete remediation.' },
      { title: 'Incident response planning', desc: 'A rehearsed plan, so the first time isn’t during a live incident.' },
      { title: 'Security training', desc: 'Practical sessions for developers and staff on the attacks you’ll face.' },
    ],
    tech: ['OWASP', 'SOC 2', 'PIPEDA', 'Penetration testing', 'Threat modelling'],
    faqs: [
      {
        q: 'We’re small. Are we really a target?',
        a: 'Most attacks are opportunistic and automated. They scan for known vulnerabilities at scale and don’t check your revenue first. Small organisations are often hit exactly because they assume they won’t be.',
      },
      {
        q: 'What does an audit produce?',
        a: 'A prioritised findings report with clear reproduction steps, business-risk rating and specific remediation guidance, plus an executive summary for people who need the decision, not the detail.',
      },
      {
        q: 'Can you help after a breach?',
        a: 'We can assist with containment, forensics and hardening. If you’re in an active incident, contact us immediately and involve your legal counsel and insurer in parallel.',
      },
    ],
  },
];
